import { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { PAY_METHOD, todayISO } from "@/lib/util";
import { Field } from "@/components/common";
import Modal from "@/components/Modal";
import FeeList, { Totals } from "@/components/FeeList";
import PdfActions from "@/components/PdfActions";
import { FileInput } from "@/pages/admin/ui";
import DateInput from "@/components/DateInput";

export default function FeeManager({ driver, s, reload }) {
  const [types, setTypes] = useState([]);
  const [m, setM] = useState(null);
  const [f, setF] = useState({});
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/settings").then((r) => setTypes(r.data.fee_types)).catch(() => {}); }, []);
  const open = (kind, target) => {
    setFile(null);
    setF(kind === "fee" ? { fee_type: types[0]?.key || "", description: "", amount: "", date: todayISO(), due_date: "" }
      : kind === "pay" ? { amount: "", date: todayISO(), method: "cash", note: "" } : kind === "due" ? { due_date: target.due_date || "" } : { amount: "", reason: "" });
    setM({ kind, target });
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async () => {
    setBusy(true);
    try {
      if (m.kind === "fee") await api.post("/fees", { ...f, driver_id: driver.id });
      if (m.kind === "pay") {
        const fd = new FormData();
        Object.entries(f).forEach(([k, v]) => fd.append(k, v));
        if (file) fd.append("receipt", file);
        await api.post(`/fees/${m.target.id}/payments`, fd);
      }
      if (m.kind === "adjust") await api.post(`/fees/${m.target.id}/adjust`, f);
      if (m.kind === "due") await api.put(`/fees/${m.target.id}/due-date`, f);
      if (m.kind === "reverse") await api.post(`/fee-payments/${m.target.id}/reverse`, { reason: f.reason });
      toast.success("تم الحفظ");
      setM(null);
      reload();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const selType = types.find((t) => t.key === f.fee_type);
  const titles = { fee: "إضافة رسم إداري", pay: "تسجيل دفعة", adjust: "قيد تصحيح على الرسم", reverse: "عكس دفعة", due: "موعد الاستحقاق" };
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2 justify-between items-center">
        <button data-testid="add-fee-btn" onClick={() => open("fee")} className="btn btn-md btn-orange"><Plus className="w-5 h-5" />إضافة رسم</button>
        <div className="w-full sm:w-auto"><PdfActions path={`/fees/statement/${driver.id}/pdf`} filename={`statement-${driver.serial}.pdf`} label="كشف الحساب" testid="admin-statement-pdf" /></div>
      </div>
      <Totals s={s} />
      <FeeList s={s} actions={{
        fee: (fee) => (<>
          <button data-testid={`fee-pay-btn-${fee.id}`} onClick={() => open("pay", fee)} className="btn btn-sm btn-orange">تسجيل دفعة</button>
          <button data-testid={`fee-adjust-btn-${fee.id}`} onClick={() => open("adjust", fee)} className="btn btn-sm btn-ghost">قيد تصحيح</button>
          <button data-testid={`fee-due-btn-${fee.id}`} onClick={() => open("due", fee)} className="btn btn-sm btn-ghost">{fee.due_date ? `يستحق ${fee.due_date}` : "موعد الاستحقاق"}</button>
        </>),
        payment: (p) => !p.reversal_of && !p.reversed && <button data-testid={`fee-reverse-${p.id}`} onClick={() => open("reverse", p)} className="text-xs text-red-600 underline">عكس</button>,
      }} />
      <Modal open={!!m} onClose={() => setM(null)} title={m ? titles[m.kind] : ""} testid="fee-modal">
        {m && (
          <div className="space-y-3">
            {m.kind === "fee" && (<>
              <Field label="نوع الرسم"><select data-testid="fee-type-select" className="field" value={f.fee_type} onChange={set("fee_type")}>
                {types.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}</select></Field>
              {selType && !selType.reviewed && <p className="text-sm text-orange-700 bg-orange-50 rounded-lg p-2">تنبيه: هذا النوع لم تتم مراجعته قانونياً بعد. تأكد من جواز تحميله على السائق وفق القانون والعقد.</p>}
              <Field label="الوصف"><input data-testid="fee-desc" className="field" value={f.description} onChange={set("description")} /></Field>
            </>)}
            {m.kind !== "reverse" && m.kind !== "due" && <Field label={m.kind === "adjust" ? "قيمة التصحيح (سالب للتخفيض)" : "المبلغ (د.ك)"}>
              <input data-testid="fee-amount" className="field ltr text-left" inputMode="decimal" placeholder="0.000" value={f.amount} onChange={set("amount")} /></Field>}
            {(m.kind === "fee" || m.kind === "pay") && <Field label="التاريخ"><DateInput testid="fee-date" value={f.date} onChange={set("date")} /></Field>}
            {(m.kind === "fee" || m.kind === "due") && <Field label="موعد الاستحقاق / التجديد" hint="يصلك تنبيه قبله بـ 30 يوماً إذا لم يُدفع الرسم"><DateInput testid="fee-due-date" value={f.due_date} onChange={set("due_date")} /></Field>}
            {m.kind === "pay" && (<>
              <Field label="طريقة الدفع"><select data-testid="fee-method" className="field" value={f.method} onChange={set("method")}>
                {Object.entries(PAY_METHOD).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></Field>
              <Field label="ملاحظة"><input className="field" value={f.note} onChange={set("note")} /></Field>
              <Field label="صورة الإيصال (اختياري)"><FileInput testid="fee-receipt" onChange={setFile} /></Field>
            </>)}
            {(m.kind === "adjust" || m.kind === "reverse") && <Field label="السبب" required><input data-testid="fee-reason" className="field" value={f.reason} onChange={set("reason")} /></Field>}
            <button data-testid="fee-submit" disabled={busy} onClick={submit} className="btn btn-lg btn-orange w-full">حفظ</button>
          </div>
        )}
      </Modal>
    </div>
  );
}
